import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store/react";
import { getAuth, onIdTokenChanged } from "firebase/auth";

import { AuthUserType } from "@/domain/auth";
import { getLoginMethod } from "@/services/firebase";
import mixpanel, {
  initSessionReplay,
  stopSessionReplay,
} from "@/services/mixpanel";
import { setSentryUser } from "@/services/sentry";

type AuthStoreContextType = {
  isAuthenticated?: boolean;
  idToken?: string | null;
  currentUserId?: string | null;
  authEmail?: string | null;
  loginMethod?: string | null;
};

const authStore = createStore({
  context: {
    isAuthenticated: undefined, // Set to undefined initially, so we can render a loading state during initial auth check
  } as AuthStoreContextType,
  on: {
    // eslint-disable-next-line max-params
    authenticated: (context, event: AuthUserType, enqueue) => {
      // Enqueue side effect to set the Sentry user and identify in Mixpanel
      // Only the Firebase UID is used — no PHI (email, name, etc.) is sent
      enqueue.effect(() => {
        void setSentryUser(event);
        void mixpanel.identify(event.id);

        if (context.currentUserId !== event.id) {
          void initSessionReplay(event.id);
        }
      });

      const { id: currentUserId, idToken, email: authEmail } = event;
      const loginMethod: string = event.loginMethod;

      return {
        currentUserId,
        isAuthenticated: true,
        idToken,
        authEmail,
        loginMethod,
      };
    },
    // eslint-disable-next-line max-params
    unauthenticated: (_context, _event, enqueue) => {
      // Enqueue side effect to remove the Sentry user and reset Mixpanel
      enqueue.effect(() => {
        void setSentryUser(null);
        // Checks Mixpanel's stored identity rather than this store's state: a
        // session invalidated while the app was closed arrives here before any
        // "authenticated" event, but the previous user is still identified.
        void mixpanel.resetIfIdentified();
        stopSessionReplay();
      });

      return {
        session: null,
        isAuthenticated: false,
        currentUserId: null,
        idToken: null,
        authEmail: null,
        loginMethod: null,
      };
    },
  },
});

// Selectors
export const useIsAuthenticated = () =>
  useSelector(authStore, state => state.context.isAuthenticated);
export const useCurrentUserAuthId = () =>
  useSelector(authStore, state => state.context.currentUserId ?? null);
export const useAuthEmail = () =>
  useSelector(authStore, state => state.context.authEmail ?? null);
export const useLoginMethod = () =>
  useSelector(authStore, state => state.context.loginMethod ?? null);

// Listeners
export const setupAuthListener = () => {
  const auth = getAuth();

  return onIdTokenChanged(auth, user => {
    if (user) {
      const uid = user.uid;

      void user.getIdToken().then(idToken => {
        authStore.send({
          type: "authenticated",
          id: uid,
          idToken,
          email: user.email ?? "",
          loginMethod: getLoginMethod(user.providerData.map(p => p.providerId)),
        });
      });
    } else {
      authStore.send({ type: "unauthenticated" });
    }
  });
};
